/**
 * The last market card's subject — the market that ended at the current anchor —
 * read from whichever source still describes it.
 *
 * **Three sources, because each one lives a different length of time.**
 * `market.{id}` is the live one and the richest, and it is dropped about thirty
 * seconds into the next market (`rollHolds`, `RETIRE`). The series rail
 * (`series.{id}`) keeps the market for as long as the rail does, and picks up
 * the full object on `market_resolved` and the state on `market_settled` — so
 * it is what the card reads for the second half of the minute. And the
 * account's own `settlements` row carries the strike and the settlement price
 * for any market it held. The card is on screen for the whole minute, so it
 * reads all three and lets the newest `state_seq` decide the state.
 *
 * **The settlement price has one more source, and it is the published identity
 * rather than a guess.** `settle(N) ≡ strike(N+1)` (§F26): the successor's
 * strike *is* this market's settlement print, and the successor is the market
 * on screen. It is read only when the successor names this market as its
 * `prev_market_id`, so a page that is pointed somewhere else cannot borrow a
 * stranger's strike.
 *
 * **Nothing here computes money.** The card's `Net` is the settlement row's own
 * `net_pnl` (§6.2 rule 2); before that row exists the card shows what is held
 * and what it pays if it holds, which is `<ResolutionPanel>`'s forecast line and
 * the same arithmetic.
 */

import { observationFrom, type Observation } from './price'
import { isDegraded, isVoidPath, type MarketState } from './lifecycle'

type Row = Record<string, unknown>

/**
 * Where the market is, from the card's point of view.
 *
 * - `UNKNOWN` — nothing has described it yet (a cold load's first frames).
 * - `TRADING` — not expired. Only reachable on a page whose anchor is behind
 *   the venue's for a moment; the card says nothing final about it.
 * - `RESOLVING` — expired, and the print is being decided.
 * - `RESOLVED` — the result is final and the money has not all moved.
 * - `SETTLED` — the result is final and the market is paid out.
 * - `VOID` — voided or refunded: nobody won, everybody got their cost back.
 */
export type LastMarketPhase = 'UNKNOWN' | 'TRADING' | 'RESOLVING' | 'RESOLVED' | 'SETTLED' | 'VOID'

/**
 * The on-chain half of the settlement.
 *
 * - `WAITING` — the result is not final, so there is nothing to pay yet.
 * - `SETTLING` — the result is final and the payout transactions are on their way.
 * - `SETTLED` — paid.
 * - `NOTHING` — nobody traded, so the market never touches the chain.
 * - `VOID` — refunded rather than paid.
 */
export type ChainStep = 'WAITING' | 'SETTLING' | 'SETTLED' | 'NOTHING' | 'VOID'

export interface LastMarketInput {
  marketId: string
  /** `market.{id}`, while it is held. */
  market: Row | null
  /** This market's entry on the series rail. */
  railEntry: Row | null
  /** The market on screen — the one anchored where this one expired. */
  successor: Row | null
  /** This account's `settlements` row for the market, once it settled. */
  settlement: Row | null
  /** This account's `positions` row for the market. */
  position: Row | null
}

export interface LastMarketView {
  marketId: string
  state: MarketState | null
  phase: LastMarketPhase
  degraded: boolean
  /** Final only — `null` until the venue says `RESOLVED`. */
  outcome: 'UP' | 'DOWN' | null
  strike: Observation | null
  /** The final print, once known. `null` while resolving: the card's live
   *  estimate is a separate thing and is never stored here. */
  settlePrice: Observation | null
  expiryAtMs: number | null
  /** The instant the settlement print is stamped. */
  printAtMs: number | null
  settlementWindowStartMs: number | null
  resolveEtaMs: number | null
  /** `true`/`false` from `trade_count`; `null` where nothing has said. */
  traded: boolean | null
  chain: ChainStep
  /** The final chunk of the settlement, from this account's own row. */
  txHash: string | null
  settledAtMs: number | null
  mine: MyResult | null
}

export type MyResult =
  | {
      kind: 'SETTLED'
      won: boolean
      outcome: string | null
      /** `shares_won` for a winner, `shares_lost` otherwise. */
      shares: string | null
      myOutcome: string | null
      costBasis: string
      payout: string
      netPnl: string
    }
  | {
      kind: 'HELD'
      upShares: string | null
      downShares: string | null
      costBasis: string | null
    }

const RESOLVING: ReadonlySet<string> = new Set(['EXPIRED', 'RESOLVING', 'RESOLVING_DEGRADED'])

export function lastMarketView(input: LastMarketInput): LastMarketView {
  const { marketId, market, railEntry, successor, settlement, position } = input

  // The newer of the two descriptions wins the state. Both follow the same
  // lifecycle, so `state_seq` orders them; a missing one loses to a present one.
  const state =
    newerState(market, railEntry) ??
    (settlement ? (settlement.outcome === 'VOID' ? 'REFUNDED' : 'SETTLED') : null)
  const resolution = (objectOf(market, 'resolution') ?? objectOf(railEntry, 'resolution')) as Row | null

  const phase = phaseOf(state)
  const final = phase === 'RESOLVED' || phase === 'SETTLED'

  const outcome =
    final ?
      (upDown(resolution?.outcome) ?? upDown(railEntry?.outcome) ?? upDown(settlement?.outcome))
    : null

  const strike =
    observationFrom(market?.strike) ??
    observationFrom(railEntry?.strike) ??
    observationFrom(resolution?.strike_price) ??
    observationFrom(settlement?.strike_price)

  const chained =
    successor !== null && successor.prev_market_id === marketId ? observationFrom(successor.strike) : null
  const settlePrice =
    final ?
      (observationFrom(resolution?.settlement_price) ?? observationFrom(settlement?.settlement_price) ?? chained)
    : null

  const expiryAtMs = numberOf(market, 'expiry_at_ms') ?? numberOf(railEntry, 'expiry_at_ms')
  const tradeCount = numberOf(market, 'trade_count') ?? numberOf(railEntry, 'trade_count')
  const traded = tradeCount !== null ? tradeCount > 0 : settlement !== null || hasShares(position) ? true : null

  return {
    marketId,
    state,
    phase,
    degraded: state !== null && isDegraded(state),
    outcome,
    strike,
    settlePrice,
    expiryAtMs,
    printAtMs: numberOf(market, 'settlement_window_end_ms') ?? numberOf(railEntry, 'settlement_window_end_ms') ?? expiryAtMs,
    settlementWindowStartMs:
      numberOf(market, 'settlement_window_start_ms') ?? numberOf(railEntry, 'settlement_window_start_ms') ?? expiryAtMs,
    resolveEtaMs: numberOf(market, 'resolve_eta_ms') ?? numberOf(railEntry, 'resolve_eta_ms'),
    traded,
    chain: chainOf(phase, traded),
    txHash: stringOf(settlement, 'redemption_tx_hash'),
    settledAtMs:
      numberOf(settlement, 'settled_at_ms') ??
      numberOf(market, 'settled_at_ms') ??
      numberOf(railEntry, 'settled_at_ms') ??
      numberOf(resolution, 'settled_at_ms'),
    mine: myResult(settlement, position, outcome),
  }
}

function phaseOf(state: MarketState | null): LastMarketPhase {
  if (state === null) return 'UNKNOWN'
  if (isVoidPath(state) || state === 'CANCELLED') return 'VOID'
  if (state === 'SETTLED') return 'SETTLED'
  if (state === 'RESOLVED') return 'RESOLVED'
  if (RESOLVING.has(state)) return 'RESOLVING'
  return 'TRADING'
}

function chainOf(phase: LastMarketPhase, traded: boolean | null): ChainStep {
  switch (phase) {
    case 'VOID':
      return 'VOID'
    case 'RESOLVED':
      return traded === false ? 'NOTHING' : 'SETTLING'
    case 'SETTLED':
      return traded === false ? 'NOTHING' : 'SETTLED'
    default:
      return 'WAITING'
  }
}

function myResult(settlement: Row | null, position: Row | null, outcome: 'UP' | 'DOWN' | null): MyResult | null {
  if (settlement) {
    const won = settlement.won === true
    return {
      kind: 'SETTLED',
      won,
      outcome: stringOf(settlement, 'outcome'),
      shares: won ? stringOf(settlement, 'shares_won') : stringOf(settlement, 'shares_lost'),
      // The side held, where the row does not name it: a winner held the
      // outcome and a loser held the other side.
      myOutcome:
        stringOf(settlement, 'my_outcome') ??
        (outcome === null ? null
        : won ? outcome
        : outcome === 'UP' ? 'DOWN'
        : 'UP'),
      costBasis: stringOf(settlement, 'cost_basis') ?? '0.000000',
      payout: stringOf(settlement, 'payout') ?? '0.000000',
      netPnl: stringOf(settlement, 'net_pnl') ?? '0.000000',
    }
  }
  if (position && hasShares(position)) {
    return {
      kind: 'HELD',
      upShares: positive(stringOf(position, 'up_shares')),
      downShares: positive(stringOf(position, 'down_shares')),
      costBasis: stringOf(position, 'cost_basis'),
    }
  }
  return null
}

function newerState(a: Row | null, b: Row | null): MarketState | null {
  const stateA = typeof a?.state === 'string' ? (a.state as MarketState) : null
  const stateB = typeof b?.state === 'string' ? (b.state as MarketState) : null
  if (stateA === null) return stateB
  if (stateB === null) return stateA
  return (numberOf(b, 'state_seq') ?? -1) > (numberOf(a, 'state_seq') ?? -1) ? stateB : stateA
}

function hasShares(position: Row | null): boolean {
  return positive(stringOf(position, 'up_shares')) !== null || positive(stringOf(position, 'down_shares')) !== null
}

/** The decimal string, or `null` when it is zero, negative or absent. */
function positive(value: string | null): string | null {
  if (value === null) return null
  return /^[0.]*$/.test(value.trim()) || value.trim().startsWith('-') ? null : value
}

function upDown(value: unknown): 'UP' | 'DOWN' | null {
  return value === 'UP' || value === 'DOWN' ? value : null
}

function objectOf(source: Row | null, field: string): unknown {
  const value = source?.[field]
  return typeof value === 'object' && value !== null ? value : null
}

function numberOf(source: Row | null, field: string): number | null {
  const value = source?.[field]
  return typeof value === 'number' ? value : null
}

function stringOf(source: Row | null, field: string): string | null {
  const value = source?.[field]
  return typeof value === 'string' ? value : null
}
